import React, { useEffect, useState, useCallback } from "react";
import api, { errMsg } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Video, CalendarClock, ArrowLeft, ListChecks, CheckCircle2, FileText, Upload, Download,
  Loader2, Send, DoorOpen,
} from "lucide-react";
import JitsiRoom from "@/pages/meetings/JitsiRoom";

const fmt = (iso) => { if (!iso) return "To be scheduled"; try { return new Date(iso).toLocaleString([], { dateStyle: "full", timeStyle: "short" }); } catch { return iso; } };

export default function PortalMeetings() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sel, setSel] = useState(null);
  const [detail, setDetail] = useState(null);
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);
  const [video, setVideo] = useState(null);
  const [joining, setJoining] = useState(false);

  const load = useCallback(async () => {
    try { const { data } = await api.get("/portal/meetings"); setData(data); }
    catch (e) { toast.error(errMsg(e, "Could not load meetings.")); } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const openMeeting = async (id) => {
    setSel(id); setDetail(null); setVideo(null);
    try { const { data } = await api.get(`/portal/meetings/${id}`); setDetail(data); }
    catch (e) { toast.error(errMsg(e, "Could not open meeting.")); setSel(null); }
  };
  const joinVideo = async () => {
    setJoining(true);
    try { const { data } = await api.get(`/portal/meetings/${sel}/video`); setVideo(data.video); }
    catch (e) { toast.error(errMsg(e, "Your host hasn't started the video yet. Please try again shortly.")); }
    finally { setJoining(false); }
  };
  const download = async (d) => {
    try { const res = await api.get(`/portal/room-documents/${d.id}/download`, { responseType: "blob" }); const url = URL.createObjectURL(res.data); window.open(url, "_blank"); setTimeout(() => URL.revokeObjectURL(url), 60000); }
    catch { toast.error("Could not open file."); }
  };
  const upload = async (file, docId) => {
    if (!file) return; setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file); fd.append("name", file.name); fd.append("doc_type", "other"); if (docId) fd.append("doc_id", docId);
      await api.post(`/portal/meetings/${sel}/documents/upload`, fd); toast.success("Uploaded. Thank you!"); openMeeting(sel);
    } catch (e) { toast.error(errMsg(e, "Upload failed.")); } finally { setBusy(false); }
  };
  const sendMsg = async () => {
    if (!msg.trim()) return; setBusy(true);
    try { await api.post(`/portal/meetings/${sel}/message`, { text: msg.trim() }); setMsg(""); toast.success("Message sent to your guide."); }
    catch (e) { toast.error(errMsg(e, "Message failed.")); } finally { setBusy(false); }
  };

  if (loading) return <div className="space-y-3"><Skeleton className="h-24" /><Skeleton className="h-24" /></div>;

  // Detail view
  if (sel && detail) {
    const m = detail.meeting; const docs = detail.documents || [];
    return (
      <div data-testid="portal-meeting-detail">
        <button onClick={() => { setSel(null); setDetail(null); }} className="inline-flex items-center gap-1.5 text-[15px] text-[#1C4358] hover:underline mb-4"><ArrowLeft size={16} /> All my meetings</button>
        <h2 className="font-display text-2xl font-bold text-[#252221]">{m.title}</h2>
        <p className="text-[15px] text-muted-foreground mt-1 flex items-center gap-2 flex-wrap"><CalendarClock size={15} /> {fmt(m.scheduled_at)} · with {m.host_name}</p>

        {video ? (
          <div className="mt-4" data-testid="portal-video-live">
            <JitsiRoom video={video} onLeave={() => setVideo(null)} height={440} />
            <button onClick={() => setVideo(null)} className="mt-1 text-[13px] text-[#9C1E22] hover:underline" data-testid="portal-leave-video-btn">Leave video</button>
          </div>
        ) : (
          <Card className="p-5 mt-4 border-[#E6EDF2] bg-[#0f2733] text-white text-center" data-testid="portal-video-shell">
            <Video size={26} className="mx-auto mb-2 opacity-80" />
            <div className="font-display font-semibold text-[16px]">Your video meeting</div>
            <div className="text-[13px] text-white/70 mt-1">Join the video when your guide is ready. Recording is off.</div>
            <Button onClick={joinVideo} disabled={joining} className="mt-3 bg-[#9C1E22] hover:bg-[#861A1D] text-white gap-1.5 rounded-full h-12 px-6" data-testid="portal-join-video-btn">
              {joining ? <Loader2 size={16} className="animate-spin" /> : <Video size={16} />} Join video
            </Button>
            <div className="text-[12px] text-white/50 mt-2">Host: {m.host_name}</div>
          </Card>
        )}

        {m.has_recap && m.recap && (
          <Card className="p-5 mt-4 border-[#E6EDF2] bg-[#F1F5F8]" data-testid="portal-meeting-recap">
            <div className="flex items-center gap-1.5 font-display font-semibold text-[#1C4358] mb-2"><FileText size={18} /> Meeting recap</div>
            <p className="text-[15px] text-[#252221] leading-relaxed whitespace-pre-line">{m.recap.summary}</p>
            {m.recap.next_meeting_date && <div className="mt-3 text-[14px] text-[#1C4358]"><b>Suggested next meeting:</b> {fmt(m.recap.next_meeting_date)}</div>}
          </Card>
        )}

        {(m.checklist || []).length > 0 && (
          <Card className="p-5 mt-4 border-[#E6EDF2]">
            <div className="flex items-center gap-1.5 font-display font-semibold text-[#1C4358] mb-2"><ListChecks size={18} /> Your next steps</div>
            {m.checklist.map((c, i) => (
              <div key={i} className="flex items-center gap-2 text-[16px] py-0.5"><CheckCircle2 size={18} className={c.done ? "text-[#2F7D5A]" : "text-[#D6E0E7]"} /><span className={c.done ? "line-through text-[#9AA5B1]" : "text-[#252221]"}>{c.label}</span></div>
            ))}
          </Card>
        )}

        <Card className="p-5 mt-4 border-[#E6EDF2]" data-testid="portal-meeting-documents">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-1.5 font-display font-semibold text-[#1C4358]"><FileText size={18} /> Documents</div>
            <label className="inline-flex items-center gap-1.5 h-10 px-4 rounded-full bg-[#1C4358] hover:bg-[#163545] text-white text-[14px] font-medium cursor-pointer" data-testid="portal-doc-upload">
              {busy ? <Loader2 size={15} className="animate-spin" /> : <Upload size={15} />} Upload a document
              <input type="file" className="hidden" onChange={(e) => e.target.files?.[0] && upload(e.target.files[0])} />
            </label>
          </div>
          {docs.length === 0 ? <p className="text-[15px] text-muted-foreground">No documents yet.</p> : (
            <div className="space-y-2">
              {docs.map((d) => (
                <div key={d.id} className="flex items-center gap-3 rounded-xl border border-[#E6EDF2] p-3">
                  <div className="min-w-0 flex-1"><div className="text-[15px] font-medium text-[#252221] truncate">{d.name}</div><div className="text-[13px] text-muted-foreground">{d.uploaded ? (d.status_label || "Uploaded") : "Requested — please upload"}</div></div>
                  {d.uploaded ? <button onClick={() => download(d)} className="p-2.5 rounded-full hover:bg-[rgba(28,67,88,0.08)] text-[#1C4358]" data-testid={`portal-doc-download-${d.id}`}><Download size={18} /></button>
                    : <label className="h-10 px-3 rounded-full bg-[#2F7D5A] text-white text-[14px] font-medium inline-flex items-center gap-1.5 cursor-pointer" data-testid={`portal-doc-fulfill-${d.id}`}><Upload size={15} /> Upload<input type="file" className="hidden" onChange={(e) => e.target.files?.[0] && upload(e.target.files[0], d.id)} /></label>}
                </div>
              ))}
            </div>
          )}
        </Card>

        <Card className="p-5 mt-4 border-[#E6EDF2]">
          <div className="font-display font-semibold text-[#1C4358] mb-2">Message your guide</div>
          <Textarea value={msg} onChange={(e) => setMsg(e.target.value)} rows={3} placeholder="Ask a question or share an update…" className="text-[15px]" data-testid="portal-meeting-message-input" />
          <Button onClick={sendMsg} disabled={busy} className="mt-2 bg-[#9C1E22] hover:bg-[#861A1D] text-white gap-1" data-testid="portal-meeting-message-send"><Send size={15} /> Send</Button>
        </Card>
      </div>
    );
  }

  // List view
  const upcoming = data?.upcoming || []; const past = data?.past || [];
  const Row = (m) => (
    <button key={m.id} onClick={() => openMeeting(m.id)} data-testid={`portal-meeting-${m.id}`}
      className="w-full text-left rounded-xl border border-[#E6EDF2] p-4 bg-white hover:border-[#1C4358]/40 transition-colors flex items-center gap-3">
      <span className="h-11 w-11 rounded-xl bg-[#1C4358]/8 text-[#1C4358] flex items-center justify-center shrink-0"><Video size={20} /></span>
      <span className="min-w-0 flex-1"><span className="block text-[16px] font-semibold text-[#252221] truncate">{m.title}</span><span className="block text-[14px] text-muted-foreground">{fmt(m.scheduled_at)} · {m.host_name}</span></span>
      <DoorOpen size={18} className="text-[#9C1E22] shrink-0" />
    </button>
  );

  return (
    <div data-testid="portal-meetings-page">
      <h2 className="font-display text-2xl font-bold text-[#252221] mb-1">Your meetings</h2>
      <p className="text-[15px] text-muted-foreground mb-5">Join a meeting, review documents, and see your next steps — all in one place.</p>
      {upcoming.length === 0 && past.length === 0 ? (
        <Card className="p-10 text-center text-muted-foreground border-[#E6EDF2]" data-testid="portal-meetings-empty">You don't have any meetings yet. Your MovingMyMama guide will invite you here.</Card>
      ) : (
        <div className="space-y-6">
          {upcoming.length > 0 && <div><div className="text-[13px] font-bold uppercase tracking-wide text-[#1C4358]/70 mb-2">Upcoming</div><div className="space-y-2.5">{upcoming.map(Row)}</div></div>}
          {past.length > 0 && <div><div className="text-[13px] font-bold uppercase tracking-wide text-[#1C4358]/70 mb-2">Past</div><div className="space-y-2.5">{past.map(Row)}</div></div>}
        </div>
      )}
    </div>
  );
}
